import { Link } from "react-router-dom";
import { Phone, ArrowUpRight, Wrench, KeyRound, AlertOctagon, Hammer, Check, Clock } from "lucide-react";
import { Overline, SectionHeader } from "@/components/Section";
import CTABand from "@/components/CTABand";

const IMG = "https://images.pexels.com/photos/17124735/pexels-photo-17124735.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";

const SITUATIONS = [
    { icon: KeyRound, t: "Clé cassée dans la serrure", d: "Extraction du fragment et restauration du mécanisme — sans casser la porte." },
    { icon: Wrench, t: "Mécanisme grippé / bloqué", d: "Démontage, nettoyage, lubrification et remplacement des pièces usées." },
    { icon: AlertOctagon, t: "Tentative d'effraction", d: "Mise en sécurité immédiate, expertise et remplacement si nécessaire." },
    { icon: Hammer, t: "Porte / cylindre HS", d: "Diagnostic complet et remplacement de cylindre ou de serrure complète." },
];

const Depannage = () => {
    return (
        <>
            <section className="relative border-b border-black/10 grid-bg" data-testid="depannage-hero">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 lg:pt-20 pb-16 lg:pb-20">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                        <div className="lg:col-span-7 fade-up fade-up-1">
                            <Overline className="mb-5">◉ Service · 24h/24</Overline>
                            <h1 className="font-display font-bold uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
                                Dépannage<br />serrurier<br /><span className="text-[#E63946]">jour & nuit.</span>
                            </h1>
                            <p className="mt-7 text-base sm:text-lg text-zinc-600 leading-relaxed max-w-xl">
                                Une serrure qui ne fonctionne plus, une clé cassée, une effraction, un cylindre HS ?
                                Atelier Beauchamp se déplace dans la commune pour réparer ou sécuriser votre installation, à toute heure.
                            </p>
                            <div className="mt-9 flex flex-col sm:flex-row gap-3">
                                <a href="tel:+33649501843" data-testid="depannage-call-btn" className="inline-flex items-center justify-between gap-4 bg-[#E63946] hover:bg-[#D62828] text-white px-6 py-4 transition-colors">
                                    <span className="flex items-center gap-3"><Phone className="h-5 w-5" strokeWidth={2.4} /><span className="font-display font-bold text-lg tracking-tight">06 49 50 18 43</span></span>
                                    <ArrowUpRight className="h-5 w-5" />
                                </a>
                                <Link to="/contact" className="inline-flex items-center justify-between gap-4 bg-[#0A0A0A] text-white px-6 py-4 hover:bg-zinc-800 transition-colors">
                                    <span className="font-semibold text-sm uppercase tracking-[0.15em]">Devis gratuit</span>
                                    <ArrowUpRight className="h-5 w-5" />
                                </Link>
                            </div>
                        </div>
                        <div className="lg:col-span-5 fade-up fade-up-2">
                            <img src={IMG} alt="Artisan serrurier limant une clé pour réparation" className="w-full h-[380px] sm:h-[480px] object-cover border border-black/10" loading="eager" />
                        </div>
                    </div>
                </div>
            </section>

            {/* SITUATIONS */}
            <section className="py-20 lg:py-28 bg-white border-b border-black/10" data-testid="depannage-situations">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <SectionHeader
                        kicker="◉ Cas traités"
                        title="Toutes les pannes de serrurerie."
                        lead="Du grain de sable dans le mécanisme à la mise en sécurité après effraction."
                    />
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5">
                        {SITUATIONS.map((s) => (
                            <div key={s.t} className="bg-white border border-black/10 p-7 sm:p-9 flex gap-5 hover:border-black transition-colors" data-testid={`depannage-situation-${s.t}`}>
                                <div className="h-12 w-12 grid place-items-center bg-[#0A0A0A] text-white flex-shrink-0">
                                    <s.icon className="h-5 w-5" strokeWidth={2.2} />
                                </div>
                                <div>
                                    <h3 className="font-display font-bold text-lg sm:text-xl uppercase tracking-tight">{s.t}</h3>
                                    <p className="mt-2 text-sm sm:text-base text-zinc-600 leading-relaxed">{s.d}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* TRANSPARENCE TARIF */}
            <section className="py-20 lg:py-28" data-testid="depannage-pricing-info">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
                        <div className="lg:col-span-5">
                            <Overline className="mb-4">◉ Engagement</Overline>
                            <h2 className="font-display font-bold uppercase text-3xl sm:text-4xl lg:text-5xl tracking-tight">
                                Tarif annoncé<br />avant intervention.
                            </h2>
                            <p className="mt-5 text-base sm:text-lg text-zinc-600 leading-relaxed">
                                Atelier Beauchamp s'engage à vous communiquer un tarif clair dès le diagnostic téléphonique.
                                Le devis est confirmé sur place avant tout démarrage : aucun frais caché, aucune mauvaise surprise.
                            </p>
                            <div className="mt-7 flex items-center gap-3 text-sm">
                                <Clock className="h-4 w-4 text-[#E63946]" strokeWidth={2.4} />
                                <span className="text-zinc-700 font-medium">Déplacement rapide dans la commune</span>
                            </div>
                        </div>
                        <div className="lg:col-span-7">
                            <div className="border border-black/10 bg-white">
                                {[
                                    { k: "Diagnostic téléphonique", v: "Gratuit" },
                                    { k: "Devis sur place", v: "Gratuit & sans engagement" },
                                    { k: "Ouverture porte claquée", v: "Sur demande" },
                                    { k: "Réparation / dépannage", v: "Sur diagnostic" },
                                    { k: "Remplacement de cylindre", v: "Sur diagnostic" },
                                    { k: "Pose de serrure A2P", v: "Sur diagnostic" },
                                    { k: "Pose de porte blindée", v: "Sur étude" },
                                ].map((row) => (
                                    <div key={row.k} className="flex items-center justify-between px-5 sm:px-7 py-4 sm:py-5 border-b border-black/10 last:border-b-0">
                                        <div className="flex items-center gap-3">
                                            <Check className="h-4 w-4 text-[#E63946]" strokeWidth={2.6} />
                                            <span className="text-sm sm:text-base font-medium">{row.k}</span>
                                        </div>
                                        <span className="text-sm sm:text-base text-zinc-600 font-medium">{row.v}</span>
                                    </div>
                                ))}
                            </div>
                            <p className="mt-4 text-xs uppercase tracking-[0.18em] text-zinc-500 font-semibold">
                                Tarifs précis communiqués sur appel selon le diagnostic.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <CTABand title="Une panne, une effraction, un doute ?" subtitle="Appelez Atelier Beauchamp — diagnostic et tarif annoncés au téléphone." />
        </>
    );
};

export default Depannage;
